import { useEffect, useId, useRef, useState, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowRight, Mic, Square } from "lucide-react";
import { cn } from "../../../lib/utils";
import { trackHomeEvent } from "../../../lib/homeAnalytics";
import { useSpeechInput } from "../../../hooks/useSpeechInput";
import { Dialog, DialogContent, DialogTitle } from "../../ui/dialog";
import CaptureSheet from "../../capture/CaptureSheet";
import TodayOwl, { useOwlMood } from "./TodayOwl";

// Today's one focus (spec 2026-11-04 §2.2–2.3): a greeting, a question by
// local time, and one quiet journal-like input. → opens CaptureSheet exactly
// as QuickInputCard does, so the whole note is kept and the distress check
// is unchanged (dictated text included).

export type DayPart = "morning" | "afternoon" | "evening";
export const dayPartOf = (d: Date): DayPart => (d.getHours() < 12 ? "morning" : d.getHours() < 17 ? "afternoon" : "evening");

const STARTERS = ["happened", "puttingOff", "wantTo"] as const;
const MIN_CAPTURE = 3;
const MIN_HEIGHT = 44;
const MAX_HEIGHT = 168; // about 6 lines

/** "Something happened…" → "Something happened " (the first words, ready to continue). */
export const starterFill = (label: string): string => `${label.replace(/[….]+\s*$/u, "").trimEnd()} `;

interface Props {
  firstName: string;
  now?: () => Date;
  /** Lets For now's "Write one thing" focus the input. */
  inputRef?: RefObject<HTMLTextAreaElement | null>;
}

const TodayCapture = ({ firstName, now = () => new Date(), inputRef }: Props) => {
  const { t } = useTranslation();
  const ownRef = useRef<HTMLTextAreaElement>(null);
  const ref = inputRef ?? ownRef;
  const questionId = useId();
  const [text, setText] = useState("");
  // The text handed to the capture sheet; null = sheet closed.
  const [sheetText, setSheetText] = useState<string | null>(null);
  const speech = useSpeechInput({
    onText: setText,
    onError: () => toast.error(t("voice_error", "Voice input didn't work. Try again.")),
  });
  const part = dayPartOf(now());
  // Each saved note makes the owl cheer. The save only marks it; the cheer plays
  // once the dialog closes, where it can be seen.
  const [planted, setPlanted] = useState(0);
  const seedPending = useRef(false);
  const mood = useOwlMood(text.length > 0 || speech.listening, planted);
  const closeSheet = () => {
    setSheetText(null);
    if (seedPending.current) {
      seedPending.current = false;
      setPlanted((n) => n + 1);
    }
  };

  // One line when empty, growing to about six.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "0px";
    el.style.height = `${Math.min(Math.max(el.scrollHeight, MIN_HEIGHT), MAX_HEIGHT)}px`;
  }, [text, ref]);

  const canCapture = text.trim().length >= MIN_CAPTURE;
  const submit = () => {
    if (speech.listening) speech.stop();
    if (canCapture) setSheetText(text.trim());
  };
  const applyStarter = (label: string) => {
    const fill = starterFill(label);
    setText(fill);
    const el = ref.current;
    if (el) {
      el.focus();
      el.value = fill;
      el.setSelectionRange(fill.length, fill.length);
    }
  };

  return (
    <section aria-labelledby={questionId} className="space-y-3">
      <div className="flex items-end gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm text-muted-foreground">{t(`good_${part}`, { name: firstName })}</p>
          <h1 id={questionId} className="mt-1 font-serif text-[19px] md:text-xl leading-snug text-foreground">
            {t(`todayHome.question.${part}`)}
          </h1>
        </div>
        {/* Phones and tablets: small, beside the question, without its line. */}
        <TodayOwl part={part} mood={mood} className="-mb-1 h-16 w-14 shrink-0 md:h-20 md:w-[70px] lg:hidden" />
      </div>

      <div
        className={cn(
          "relative flex items-end gap-1 rounded-2xl border bg-card ps-4 pe-1.5 py-1.5 transition-colors focus-within:border-primary/40",
          speech.listening ? "border-destructive" : "border-border"
        )}
      >
        {/* Wide screens: in the margin before the text box, perched level with the starters, its line above. */}
        <TodayOwl
          part={part}
          mood={mood}
          bubble
          className="pointer-events-none absolute -bottom-16 end-[calc(100%+1.25rem)] hidden w-[132px] lg:flex [&>svg]:h-[150px]"
        />
        <textarea
          ref={ref}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder={t("todayHome.capture.placeholder")}
          aria-labelledby={questionId}
          rows={1}
          maxLength={5000}
          dir="auto"
          className="min-w-0 flex-1 resize-none bg-transparent py-2.5 text-[15px] leading-relaxed text-start placeholder:text-muted-foreground/60 focus:outline-none"
        />
        {speech.supported && (
          <button
            type="button"
            onClick={() => (speech.listening ? speech.stop() : speech.start(text))}
            aria-label={speech.listening ? t("todayHome.capture.stop") : t("todayHome.capture.speak")}
            className={cn(
              "inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-colors",
              speech.listening ? "bg-destructive text-destructive-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"
            )}
          >
            {speech.listening ? <Square className="h-4 w-4" aria-hidden /> : <Mic className="h-5 w-5" aria-hidden />}
          </button>
        )}
        <button
          type="button"
          onClick={submit}
          disabled={!canCapture}
          aria-label={t("todayHome.capture.send")}
          className={cn(
            "inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-colors",
            canCapture ? "bg-primary text-primary-foreground hover:bg-primary/90" : "bg-muted text-muted-foreground/60"
          )}
        >
          <ArrowRight className="h-5 w-5 rtl:rotate-180" aria-hidden />
        </button>
      </div>

      {speech.listening && (
        <p role="status" className="flex items-center gap-1.5 text-xs text-destructive">
          <span aria-hidden className="h-2 w-2 rounded-full bg-destructive motion-safe:animate-pulse" />
          {t("todayHome.capture.listening")}
        </p>
      )}

      {!text && !speech.listening && (
        <div className="flex flex-wrap gap-x-4">
          {STARTERS.map((k) => {
            const label = t(`todayHome.capture.starter.${k}`);
            return (
              <button
                key={k}
                type="button"
                onClick={() => applyStarter(label)}
                className="min-h-[44px] text-sm text-muted-foreground hover:text-foreground"
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

      <Dialog open={sheetText !== null} onOpenChange={(open) => { if (!open) closeSheet(); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogTitle className="sr-only">{t(`todayHome.question.${part}`)}</DialogTitle>
          {sheetText !== null && (
            <CaptureSheet
              text={sheetText}
              source="home"
              onSaved={(r) => {
                setText("");
                seedPending.current = true;
                trackHomeEvent("quick_capture_submitted", { classified_as: r.items[0]?.kind ?? "insight" });
              }}
              onClose={closeSheet}
            />
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default TodayCapture;
